// Import from SDK for local use, and re-export so existing app imports keep working.
import {
  NonJsonResponseError,
  isNonJsonResponseError,
  isUpstreamRequestError,
  type NonJsonResponseErrorInit,
} from '@koala/sdk/v4';

export { NonJsonResponseError, isNonJsonResponseError, type NonJsonResponseErrorInit };

export const GATEWAY_STATUSES: readonly number[] = [502];

export const RETRYABLE_STATUSES: readonly number[] = [408, 429, 500, 502, 503, 504];

/** The numeric `status` an error carries, if any (an HTTP response or an `UpstreamRequestError`). */
export const readStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

export const isStatusError = (error: unknown, statuses: readonly number[]): boolean => {
  const status = readStatus(error);
  return status != null && statuses.includes(status);
};

/**
 * Every wording the browser / RN layer uses for a transient transport failure — Safari's
 * "Load failed", Chrome's "Failed to fetch", RN's "Network request failed", Firefox's
 * "NetworkError when attempting to fetch resource.".
 *
 * Exported so callers that cannot use {@link isNetworkError} still match the same set.
 * `isNetworkError` requires a real `Error`; some SDKs hand back plain objects carrying the
 * same wording in a free-text field (see `toastErrorClassifier`), and those callers were
 * previously copying a subset of this list and silently missing the rest.
 */
export const NETWORK_ERROR_MESSAGES: readonly string[] = [
  'Load failed',
  'Failed to fetch',
  'Network request failed',
  'NetworkError when attempting to fetch resource.',
];

/**
 * Matches transient fetch failures from the browser / RN layer — e.g. Safari's
 * "Load failed", Chrome's "Failed to fetch", RN's "Network request failed".
 * Excludes intentional aborts (AbortError) so a user-cancelled request is never
 * retried.
 *
 * Modern Chrome (~v117+) appends the failed origin to the message, producing
 * e.g. `"Failed to fetch (https://prod-coo-api.chowly.io)"`. Prefix-matching
 * each wording keeps detection working across both bare and URL-suffixed forms.
 *
 * Node's `fetch` (undici) uses a different wording again — a bare
 * `TypeError: fetch failed` — which matches none of the browser prefixes and carries
 * no `status`. Until TEAM-14914 that made every server-side transient network failure
 * non-retryable everywhere: `getInitialProps`/`getServerSideProps` reads would spend
 * exactly one attempt no matter which retry helper wrapped them. Node's message is
 * lower-case and is NOT a prefix of Chrome's "Failed to fetch", so it needs its own
 * entry rather than a case-insensitive match. (TEAM-14914)
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.name === 'AbortError') return false;
  const message = error.message ?? '';
  return (
    NETWORK_ERROR_MESSAGES.some((wording) => message.startsWith(wording)) ||
    message.startsWith('fetch failed')
  );
};

/**
 * True when a request failed with no readable response at all — the transport-failure class the
 * shared connection-error copy ("We're having trouble connecting.") is for.
 *
 * Since TEAM-15165 `withJsonGuard` rethrows a browser `TypeError: Failed to fetch` / `Load failed`
 * unchanged (it no longer wraps it in an `UpstreamRequestError`), so {@link isNetworkError} catches
 * the menu-read case directly. The second clause stays defensive: a network failure that is still
 * wrapped one level deep in an `UpstreamRequestError` — e.g. from the SDK, whose message
 * ("Upstream request failed: api /menus") no longer starts with a network wording — must still read
 * as a transport failure. This unwraps one level of `cause`, the same composition
 * {@link isRetryableError} uses. A wrapped 5xx, a 4xx, or a {@link NonJsonResponseError} is a real
 * response and returns false. (TEAM-15314, TEAM-15165)
 */
export const isTransportFailure = (error: unknown): boolean =>
  isNetworkError(error) || (isUpstreamRequestError(error) && isNetworkError(error.cause));

/**
 * Default retry predicate for {@link retryWithBackoff} and React-Query
 * `retry` callbacks. Returns true for transient failures: retryable status
 * codes, network errors, and non-JSON gateway/CDN responses.
 *
 * An {@link UpstreamRequestError} is retryable when its `.status` maps to a
 * retryable HTTP code (handled by `isStatusError`) OR when it wraps a browser
 * network failure (`cause` is a network error) — without this check, wrapping
 * a `TypeError: Load failed` silently kills retries because the wrapper's
 * message no longer matches `isNetworkError`.
 */
export const isRetryableError = (error: unknown): boolean =>
  isStatusError(error, RETRYABLE_STATUSES) ||
  isNetworkError(error) ||
  isNonJsonResponseError(error) ||
  (isUpstreamRequestError(error) && isNetworkError(error.cause));
